import { useMemo } from 'react'
import styled from 'styled-components'
import { rem } from 'polished'
import ResponsiveImage from 'components/Common/ResponsiveImage'
import LoadingBox from 'components/Common/Loading/LoadingBox'
import type { DrawImagesCategory } from 'tripPlanner/hooks/api/locationImage'
import { useLocationDrawPlaceDetails } from 'tripPlanner/hooks/api/locationImage'
import type { CardOption } from './CardsList'
import Group from 'components/utils/Group'

const IMAGE_WIDTH = 180
const IMAGE_HEIGHT = 108

const ImageContainer = styled.div`
  position: relative;
  width: ${rem(IMAGE_WIDTH)};
  min-width: ${rem(IMAGE_WIDTH)};
  min-height: ${rem(IMAGE_HEIGHT)};
  align-self: stretch;
  border-radius: ${props => props.theme.borderRadius.L} 0 0 ${props => props.theme.borderRadius.L};
  overflow: hidden;
  background-color: ${props => props.theme.palette.neutral.default.seven};
`

const ImageInner = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`

const EmojiContainer = styled(Group)`
  width: ${rem(IMAGE_WIDTH)};
  min-width: ${rem(IMAGE_WIDTH)};
  min-height: ${rem(IMAGE_HEIGHT)};
  align-self: stretch;
  border-radius: ${props => props.theme.borderRadius.L} 0 0 ${props => props.theme.borderRadius.L};
  background-color: ${props => props.theme.palette.neutral.default.seven};
  font-size: ${rem(32)};
`

interface Props {
  option: CardOption
  category: DrawImagesCategory
}

function ImageCard({ option, category }: Props) {
  const locationData = useMemo(() => {
    if (!option.placeSearch) return undefined
    return {
      title: option.title,
      description: option.description,
      emoji: option.emoji,
      placeSearch: option.placeSearch,
    }
  }, [option])

  const { data: drawData, isLoading } = useLocationDrawPlaceDetails(locationData, category)

  const firstImageId = drawData?.imageIdentifiers?.[0]
  const isUrl = firstImageId?.startsWith('http')

  if (isLoading) {
    return (
      <ImageContainer>
        <LoadingBox floating />
      </ImageContainer>
    )
  }

  if (firstImageId) {
    if (isUrl) {
      return (
        <ImageContainer>
          <ImageInner>
            <img src={firstImageId} alt={option.title} />
          </ImageInner>
        </ImageContainer>
      )
    }
    return (
      <ImageContainer>
        <ImageInner>
          <ResponsiveImage
            id={firstImageId}
            aspectRatio="5:3"
            fit="center"
            alt={option.title}
          />
        </ImageInner>
      </ImageContainer>
    )
  }

  if (option.emoji) {
    return (
      <EmojiContainer direction="vertical" horizontalAlign="center" verticalAlign="center">
        {option.emoji}
      </EmojiContainer>
    )
  }

  return null
}

export default ImageCard
